// material-ui
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Modal,
    Checkbox,
    Grid,
    ToggleButtonGroup,
    ToggleButton,
    Button,
    Typography,
    TextField,
    Select,
    MenuItem,
    IconButton
} from '@mui/material';
import { ManageAccounts, Cancel, Refresh } from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useState, useEffect  } from 'react';
import TasklistFilter from './TasklistFilter';
import { deepPurple, grey } from '@mui/material/colors';
import useFetchData from 'hooks/useFetchData';
import { axiosInstance } from 'utils/axios';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';
import { successAlert, errorAlert, warningAlert } from 'ui-component/Toasts';
import { useTranslation } from "react-i18next";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    '& .MuiToggleButtonGroup-grouped': {
        //   margin: theme.spacing(2),

        //   '&.Mui-disabled': {
        //     border: 0,
        //   },
        '&:not(:first-of-type)': {
            borderRadius: theme.shape.borderRadius
        },
        '&:first-of-type': {
            borderRadius: theme.shape.borderRadius
        }
    }
}));

const StyledToggleButton = styled(ToggleButton)({
    border: 2,
    borderColor: deepPurple[500],
    backgroundColor: grey[300],
    '&.Mui-selected, &.Mui-selected:hover': {
        color: 'white',
        backgroundColor: deepPurple[500]
    }
});

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 1000,
    width: '90%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    padding: '20px',
    borderRadius: '10px'
};

const ResetTasklistModal = ({
    resetTasklistModalOpen,
    setResetTasklistModalOpen,
    activeTab,
    buttonName,
    customerRow,
    fetchCustomers,
    handleRefresh
}) => {
    const { t } = useTranslation();
    const token = useSelector(selectCurrentToken);

    const [view, setView] = useState('resetTasklist');
    const [customerID, setCustomerID] = useState(0);
    
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);

    const handleChange = (event, nextView) => {
        setView(nextView);
    };

    const handleOpenModal = () => {
        setView(activeTab);
        setResetTasklistModalOpen(true);
        setCustomerID(customerRow.id);
    };

    useEffect(() => {
        if (resetTasklistModalOpen && customerRow?.id) {
            setCustomerID(customerRow.id);
            setView(activeTab); // or set to 'resetTasklist' by default
        }
    }, [resetTasklistModalOpen, customerRow]);

    function ResetTasklistTab({customerID}) {
        const [resetButtonDisabled, setResetButtonDisabled] = useState(false);
        const [fetchEstimatedDisabled, setFetchEstimatedDisabled] = useState(false);

        const [estimatedPresetCustomerTasklist, setEstimatedPresetCustomerTasklist] = useState([]);
        const [estimatedTotalProfit, setEstimatedTotalProfit] = useState(0);

        const [isAllowedToReset, setIsAllowedToReset] = useState(false);
        const [isTotalCommAllowedToReset, setIsTotalCommAllowedToReset] = useState(false);
        const [resetErrorMessage, setResetErrorMessage] = useState('');
        const [resetTotalCommErrorMessage, setResetTotalCommErrorMessage] = useState('');

        const [bonusInputs, setBonusInputs] = useState({});
        const handleBonusChange = (id, newProfit) => {
            setBonusInputs(prev => ({
                ...prev,
                [id]: {
                    ...(prev[id] || { taskNumber: 0, profit: 0 }), // default structure if it doesn't exist
                    commissionPercentage: newProfit
                }
            }));
        };

        useEffect(() => {
            const total = Object.values(bonusInputs).reduce((sum, item) => {
                const val = parseFloat(item.commissionPercentage);
                return sum + (isNaN(val) ? 0 : val);
            }, 0);

            const roundedTotal = Math.round(total * 100) / 100;

            if (roundedTotal != 100){
                setResetTotalCommErrorMessage(t("customer.totalResetCommissionMustBe100"));
                setIsTotalCommAllowedToReset(false);
            }
            else{
                setResetTotalCommErrorMessage('');
                setIsTotalCommAllowedToReset(true);
            }
        }, [bonusInputs]);

        const handleEstimatedTotalProfitChange = (value) => {
            setEstimatedTotalProfit(value);
            // fetchEstimatedPresetCustomerTasklist(value);
        };

        // useEffect(() => {
        //     fetchEstimatedPresetCustomerTasklist();
        // },[]);

        const submitResetCustomerTasklist = async () => {
            try {
                const res = await axiosInstance.post(
                    '/ResetCustomerTasklistRecord',
                    {
                        customerID: customerID,
                        totalProfit: estimatedTotalProfit
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                if (res.status === 200) {
                    // submitInsertEstimatedPreSetCustomerTasklistBeforeReset();
                    successAlert('成功重置任务');
                    handleRefresh();
                } else {
                    errorAlert('发生错误');
                }
            } catch (error) {
                console.log(error);
            } finally {
                setResetTasklistModalOpen(false);
            }
        };

        const submitInsertEstimatedPreSetCustomerTasklistBeforeReset = async () => {
            if (estimatedTotalProfit == 0){
                errorAlert('Profit cannot be 0');
                return;
            }
                
            setResetButtonDisabled(true);

            const requestPayload = {
                customerID: customerID,
                totalProfit: estimatedTotalProfit,
                details: Object.entries(bonusInputs).map(([taskNumber, value]) => ({
                    taskNumber: parseInt(taskNumber),
                    commissionPercentage: parseFloat(value.commissionPercentage ?? 0)
                }))
            };

            try {
                const res = await axiosInstance.post(
                    '/Backend/Estimated/CustomerTasklist/BeforeReset',
                    requestPayload,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                if (res.status === 200) {
                    submitResetCustomerTasklist();
                    successAlert('成功重置任务');
                    handleRefresh();
                } else {
                    errorAlert('发生错误');
                    setResetButtonDisabled(false);
                }
            } catch (error) {
                console.log(error);
                setResetButtonDisabled(false);
            } finally {
            }
        };

        const fetchEstimatedPresetCustomerTasklist = async () => {
            setFetchEstimatedDisabled(true);

            try {
                const res = await axiosInstance.get(
                    `/Backend/Estimated/CustomerTasklist/BeforeReset?customerID=${customerID}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                if (res.status === 200) {
                    // const totalProfitSum = res.data.reduce((sum, item) => {
                    //     const profit = parseFloat(item.profit) || 0;
                    //     return sum + profit;
                    // }, 0);

                    // setEstimatedTotalProfit(Math.round(totalProfitSum * 100) / 100);
                    const firstItem = res.data[0];
                    setEstimatedTotalProfit(firstItem.currentPendingTaskProfit);
                    setEstimatedPresetCustomerTasklist(res.data);
                } else {
                    errorAlert('发生错误');
                }
            } catch (error) {
                console.log(error);
            } finally {
                setFetchEstimatedDisabled(false);
            }
        };

        const fetchIsAllowResetCustomerTasklist = async () => {
            try {
                const res = await axiosInstance.get(`/IsAllow/ResetCustomerTasklist?customerID=${customerID}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

                if (res.status === 200) {
                    setIsAllowedToReset(res.data);
                } else {
                    setResetErrorMessage(t("customer.pleaseCheckIsCustomerComplete"));
                }
            } catch (error) {
                console.log(error);
            }
        };

        useEffect(() => {
            fetchIsAllowResetCustomerTasklist();
            fetchEstimatedPresetCustomerTasklist();
        },[]);

        useEffect(() => {
            if (estimatedPresetCustomerTasklist.length > 0) {
                const newBonusInputs = {};
                estimatedPresetCustomerTasklist.forEach(detail => {
                    newBonusInputs[detail.taskNumber] = {
                        commissionPercentage: detail.commissionPercentage
                    };
                });
                setBonusInputs(newBonusInputs);
            }
        }, [estimatedPresetCustomerTasklist]);

        return (
            <Box>
                <Box display="flex" alignItems="center">
                    {t("customer.profit")} {" "}
                    <TextField 
                        type="number"
                        id="outlined-basic" 
                        size="small"
                        variant="outlined" 
                        value={estimatedTotalProfit}
                        onChange={(e) => handleEstimatedTotalProfitChange(e.target.value)}
                        style={{fontSize: '12px'}}
                    />
                    {/* <IconButton onClick={fetchEstimatedPresetCustomerTasklist} size="small" sx={{ ml: 1 }}>
                        <Refresh fontSize="small" />
                    </IconButton> */}
                </Box>
                <TableContainer sx={{ maxHeight: 440 }}>
                    <Table stickyHeader aria-label="sticky table" size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell key='details' style={{ minWidth: 170, py: 0.5 }}>{t("customer.comboTaskNumber")}</TableCell>
                                <TableCell key='commissionPercentage' style={{ minWidth: 170, py: 0.5 }}>
                                    {t("ambassadorLevel.comissionPercentage")}
                                </TableCell>
                                <TableCell key='profit' style={{ minWidth: 170, py: 0.5 }}>{t("customer.taskProfit")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {estimatedPresetCustomerTasklist.map((row) => {
                                return (
                                    <TableRow hover role="checkbox" tabIndex={-1} key={row.code} sx={{ height: 36 }}>
                                        <TableCell key='details' sx={{ py: 0.5, px: 1 }}>
                                            {t("customer.taskNumber1")} {row.taskNumber} {t("customer.taskNumber2")}
                                        </TableCell>
                                        <TableCell key='commissionPercentage' sx={{ py: 0.5, px: 1 }}>
                                            <TextField 
                                                type="number"
                                                id="outlined-basic" 
                                                size="small"
                                                variant="outlined" 
                                                value={bonusInputs[row.taskNumber]?.commissionPercentage ?? row.commissionPercentage ?? ''}
                                                onChange={(e) => handleBonusChange(row.taskNumber, e.target.value)}
                                                style={{fontSize: '12px'}}
                                            />
                                        </TableCell>
                                        <TableCell key='taskProfit' sx={{ py: 0.5, px: 1 }}>
                                            {((estimatedTotalProfit * (bonusInputs[row.taskNumber]?.commissionPercentage / 100)) ?? 0).toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Typography
                    sx={{
                        fontSize: '25px',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        marginTop: '30px'
                    }}
                >
                    {t("customer.confirmResetTask")} ?
                </Typography>
                <Button
                    variant="contained"
                    sx={{
                        display: 'block',
                        mx: 'auto',
                        width: '150px',
                        backgroundColor: deepPurple[500],
                        marginTop: '30px'
                    }}
                    onClick={submitInsertEstimatedPreSetCustomerTasklistBeforeReset}
                    disabled={resetButtonDisabled || !isAllowedToReset || !isTotalCommAllowedToReset}
                >
                    {t("customer.confirm")}
                </Button>

                {resetErrorMessage && (
                    <Typography
                    sx={{
                        color: 'red',
                        textAlign: 'center',
                        marginTop: '20px',
                    }}
                    >
                    {resetErrorMessage}
                    </Typography>
                )}

                {resetTotalCommErrorMessage && (
                    <Typography
                    sx={{
                        color: 'red',
                        textAlign: 'center',
                        marginTop: '20px',
                    }}
                    >
                    {resetTotalCommErrorMessage}
                    </Typography>
                )}
            </Box>
        );
    }

    function ResetTasklistRecordsTab({customerID}) {
        useEffect(() => {
            fetchResetCustomerTasklistRecords();
        },[]);

        const [resetCustomerTasklistRecords, setResetCustomerTasklistRecords] = useState([]);
        const fetchResetCustomerTasklistRecords = async () => {
            try {
                const res = await axiosInstance.get(`/Customer/ResetCustomerTasklistRecord?customerID=${customerID}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                if (res.status === 200) {
                    setResetCustomerTasklistRecords(res.data);
                }
            } catch (error) {

            }
        };

        return (
            <Box>
                <TableContainer sx={{ maxHeight: 440 }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                <TableCell key='details' style={{ minWidth: 170 }}>详情</TableCell>
                                <TableCell key='commissionPercentage' style={{ minWidth: 170 }}>重置时间</TableCell>
                                <TableCell key='totalProfit' style={{ minWidth: 170 }}>{t("customer.profit")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {resetCustomerTasklistRecords.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                                return (
                                    <TableRow hover role="checkbox" tabIndex={-1} key={row.code}>
                                        <TableCell key='details'>
                                            {t("customer.by")}: {row.createdBy} {t("customer.reset")}
                                        </TableCell>
                                        <TableCell key='commissionPercentage'>
                                            {
                                                `${row.createdDate.split('T')[0]} ${new Date(
                                                    '1970-01-01T' + row.createdDate.split('T')[1] + 'Z'
                                                ).toLocaleTimeString('en-US', {
                                                    timeZone: 'UTC',
                                                    hour12: true,
                                                    hour: 'numeric',
                                                    minute: 'numeric',
                                                    second: 'numeric'
                                                })}`
                                            }
                                        </TableCell>
                                        <TableCell key='totalProfit'>
                                            {row.totalProfit}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    }

    return (
        <>
            {/* <Button
                onClick={handleOpenModal}
                sx={{
                    border: 'none',
                    backgroundColor: deepPurple[500],
                    width: 'fit-content',
                    height: 'fit-content',
                    color: 'white',
                    '&.Mui-selected, &.Mui-selected:hover': {
                        color: deepPurple[500],
                        backgroundColor: 'white'
                    },
                    fontSize: '12px'
                }}
            >
                {buttonName}
            </Button> */}
            <Modal
                open={resetTasklistModalOpen}
                onClose={() => setResetTasklistModalOpen(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Typography id="modal-modal-title" variant="h3" component="h2">
                            {t("customer.resetTask")}
                        </Typography>
                        <Cancel onClick={() => setResetTasklistModalOpen(false)} sx={{ width: '30px', height: '30px' }} />
                    </Box>
                    <Box
                        id="modal-modal-description"
                        sx={{
                            overflowY: 'auto'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%' 
                            }}
                        >
                            <StyledToggleButtonGroup
                                value={view}
                                exclusive
                                onChange={handleChange}
                                sx={{
                                    flexDirection: { xs: 'column', md: 'row' }
                                }}
                            >
                                <StyledToggleButton value="resetTasklist" aria-label="resetTasklist">
                                    {t("customer.resetTask")}
                                </StyledToggleButton>
                                <StyledToggleButton value="resetTasklistRecords" aria-label="resetTasklistRecords">
                                    {t("customer.resetRecord")}
                                </StyledToggleButton>
                            </StyledToggleButtonGroup>
                        </Box>
                        
                        {/* <div className={view === 'resetTasklist' ? 'block' : 'hidden'}>
                            <ResetTasklistTab />
                        </div>
                        <div className={view === 'resetTasklistRecords' ? 'block' : 'hidden'}>
                            <ResetTasklistRecordsTab />
                        </div> */}
                        <Box>
                            {view === 'resetTasklist' && (
                                <>
                                    <ResetTasklistTab customerID={customerID} />
                                </>
                            )}
                            {view === 'resetTasklistRecords' && (
                                <>
                                    <ResetTasklistRecordsTab customerID={customerID} />
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default ResetTasklistModal;